import type { PuzzleTemplateId } from "../../types/domain";
import { platformerJumpCore } from "./platformerJump.ts";
import { specPuzzleTemplates } from "./specTemplates.ts";
import { trigCannonCore } from "./trigCannon.ts";
import type { ValidationResult } from "./types.ts";
import { vectorDroneCore } from "./vectorDrone.ts";
import type { PuzzleInstance, PuzzleTemplate } from "./framework.ts";

export const puzzleCoreRegistry = {
  "vector-drone": vectorDroneCore,
  "trig-cannon": trigCannonCore,
  "platformer-jump": platformerJumpCore,
  ...specPuzzleTemplates,
} as const;

export function generatePuzzleInstance(
  templateId: PuzzleTemplateId,
  config: Record<string, unknown>,
  seed: number,
): unknown {
  if (templateId === "coming-soon") return null;
  const core = puzzleCoreRegistry[templateId] as unknown;
  if (typeof core === "object" && core !== null && "generateInstance" in core) {
    return (core as PuzzleTemplate).generateInstance(config, { seed });
  }
  return (
    core as {
      generate: (config: Record<string, unknown>, seed: number) => unknown;
    }
  ).generate(config, seed);
}

export function validatePuzzleSubmission(
  templateId: PuzzleTemplateId,
  config: Record<string, unknown>,
  instance: unknown,
  submission: Record<string, unknown>,
): ValidationResult {
  if (templateId === "coming-soon") {
    return {
      success: false,
      score: 0,
      feedback: "This puzzle template is not available yet.",
    };
  }
  const core = puzzleCoreRegistry[templateId] as unknown;
  if (typeof core === "object" && core !== null && "validateAnswer" in core) {
    const template = core as PuzzleTemplate;
    const validation = template.validateAnswer(
      config,
      instance as PuzzleInstance,
      submission,
    );
    return template.scoreAttempt(validation, undefined, { seed: 0 });
  }
  return (
    core as {
      validate: (
        config: Record<string, unknown>,
        instance: unknown,
        submission: Record<string, unknown>,
      ) => ValidationResult;
    }
  ).validate(config, instance, submission);
}

export function getPuzzleTemplates() {
  return Object.values(puzzleCoreRegistry).map((template) => ({
    id: template.id,
    title: "title" in template ? template.title : template.id,
    schemaVersion: "schemaVersion" in template ? template.schemaVersion : 1,
    skills: "skills" in template ? template.skills : [],
    interaction: "interaction" in template ? template.interaction : "Interactive puzzle",
  }));
}
